import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, setToken } from "@/lib/api";
import Logo from "@/components/Logo";

export default function AcceptInvitePage() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const nav = useNavigate();
    const [preview, setPreview] = useState(null);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!token) { setErr("Missing invite token"); return; }
        api.get(`/clients/invite/${token}`)
            .then(({ data }) => setPreview(data))
            .catch((e) => setErr(formatApiError(e.response?.data?.detail) || e.message));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (password !== confirm) { setErr("Passwords don't match"); return; }
        setErr(""); setBusy(true);
        try {
            const { data } = await api.post("/clients/invite/accept", { token, password });
            setToken(data.token);
            window.location.assign("/client"); // land on client portal
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--bg)]" data-testid="accept-invite-page">
            <div className="w-full max-w-md">
                <Logo size="sm" />
                {err && !preview && (
                    <div className="mt-10 border border-[var(--danger)] bg-[var(--danger)]/10 text-[var(--danger)] p-4" data-testid="invite-error">{err}</div>
                )}
                {preview && (
                    <>
                        <div className="mt-10 border border-[var(--gold-deep)] bg-[var(--surface)] p-4 mb-8">
                            <p className="label-tiny mb-1 text-[var(--gold-bright)]">Invited by</p>
                            <p className="font-serif text-2xl">{preview.coach_name}</p>
                        </div>
                        <p className="label-tiny mb-3">/ Welcome {preview.client_name?.split(" ")[0]}</p>
                        <h2 className="font-serif text-5xl mb-2">Accept your invite.</h2>
                        <p className="text-[var(--text-muted)] text-sm mb-8">Set a password to sign in to your athlete portal.</p>
                        <form onSubmit={submit} className="space-y-5" data-testid="accept-form">
                            <div>
                                <label className="label-tiny block mb-2">Email</label>
                                <input disabled className="input-dark opacity-60" value={preview.email} />
                            </div>
                            <div>
                                <label className="label-tiny block mb-2">Password</label>
                                <input data-testid="accept-password" type="password" minLength={6} className="input-dark" value={password} onChange={(e) => setPassword(e.target.value)} required />
                            </div>
                            <div>
                                <label className="label-tiny block mb-2">Confirm password</label>
                                <input data-testid="accept-confirm" type="password" minLength={6} className="input-dark" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
                            </div>
                            {err && <div className="text-sm text-[var(--danger)]">{err}</div>}
                            <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="accept-submit">
                                {busy ? "Creating account…" : "Accept & enter portal →"}
                            </button>
                        </form>
                    </>
                )}
                <div className="gold-rule my-10" />
                <Link to="/login" className="text-sm text-[var(--text-muted)] hover:text-[var(--gold)]">Already have an account? Sign in</Link>
            </div>
        </div>
    );
}
